// Stale-tab guard (ENTRAID-261). A tab opened before a deploy keeps running
// the old bundle, and with it any link that bundle holds in memory: after
// the v2 consent fix, an open onboarding tab kept offering the old
// v1-style admin_consent prompt link (AADSTS901001) until it was reloaded.
//
// The build id is baked in at build time (VITE_BUILD_ID, set by
// deploy-portal.sh) and published next to it as /build.json (no-cache).
// Whenever the tab regains focus, becomes visible, or every few minutes, the
// page compares the two and reloads itself when a newer build is live. It
// never reloads while the user is typing into a field. Without a build id
// (local dev, tests) it does nothing.

export const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function runningBuildId() {
  return import.meta.env?.VITE_BUILD_ID || null;
}

function typing(doc) {
  const el = doc.activeElement;
  if (!el) return false;
  const tag = String(el.tagName || '').toLowerCase();
  return (tag === 'input' || tag === 'textarea') && Boolean(el.value);
}

/**
 * Checks once. Returns 'reloaded' | 'current' | 'deferred' | 'skipped'.
 * All effects are injectable for tests.
 */
export async function checkBuildFreshness({
  running = runningBuildId(),
  fetchImpl = (url, init) => fetch(url, init),
  reload = () => window.location.reload(),
  doc = document,
  pathname = typeof window === 'undefined' ? '' : window.location.pathname,
} = {}) {
  if (!running) return 'skipped';
  // The public Watch pages (/watch/review, /watch/preferences) hold a one-time token that was read from the
  // URL fragment and removed from the address bar; a reload would lose it (ENTRAID-289). The Access reviews link
  // (/access-reviews/review) does the same, and a reload would also drop the reviewer's unsaved choices.
  if (pathname.startsWith('/watch/') || pathname.startsWith('/access-reviews/')) return 'skipped';
  let live;
  try {
    const res = await fetchImpl('/build.json', { cache: 'no-store' });
    if (!res.ok) return 'skipped';
    live = (await res.json())?.build;
  } catch {
    return 'skipped';
  }
  if (typeof live !== 'string' || !live || live === running) return 'current';
  if (typing(doc)) return 'deferred';
  reload();
  return 'reloaded';
}

/** Starts the checks; returns a stop function. */
export function startBuildFreshness(options = {}) {
  if (!runningBuildId() && !options.running) return () => {};
  const check = () => { if (document.visibilityState !== 'hidden') checkBuildFreshness(options); };
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  const timer = setInterval(check, CHECK_INTERVAL_MS);
  return () => {
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
    clearInterval(timer);
  };
}
